import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const testimonios = [
  {
    quote: 'Llamé un lunes porque el toldo de mi terraza no recogía y el viernes ya estaba funcionando perfecto. Vinieron a verlo, me dieron presupuesto al momento y en dos días lo arreglaron. Profesionales y muy puntuales.',
    author: 'María G.',
    role: 'Particular, Málaga Este',
    rating: 5,
    icon: 'ri-home-smile-line',
  },
  {
    quote: 'Teníamos tres toldos en el restaurante que estaban fatal después del invierno. Nos hicieron un mantenimiento completo, cambiaron dos lonas y motorizaron uno. Desde entonces, cero problemas.',
    author: 'Carlos R.',
    role: 'Restaurante, Málaga Centro',
    rating: 5,
    icon: 'ri-restaurant-2-line',
  },
  {
    quote: 'Necesitaba un toldo a medida para una terraza con forma irregular y varias empresas me dijeron que era complicado. Estos chicos vinieron, tomaron medidas y en una semana estaba instalado. Quedó perfecto.',
    author: 'Ana L.',
    role: 'Particular, Torremolinos',
    rating: 5,
    icon: 'ri-home-smile-line',
  },
  {
    quote: 'El motor del toldo dejaba de funcionar cada vez que hacía viento. Vinieron, diagnosticaron que era un problema del sensor, lo cambiaron y ahora funciona sin fallos. Muy recomendables.',
    author: 'José M.',
    role: 'Particular, Fuengirola',
    rating: 5,
    icon: 'ri-home-smile-line',
  },
  {
    quote: 'Contraté el servicio de mantenimiento anual para la terraza de mi hotel. Cada primavera vienen, revisan todos los mecanismos, limpian las lonas y ajustan los brazos. Un trabajo impecable.',
    author: 'Patricia S.',
    role: 'Hotel, Marbella',
    rating: 5,
    icon: 'ri-hotel-line',
  },
  {
    quote: 'La lona del toldo estaba desgastada y tenía un agujero. Me dieron presupuesto en 24h, la cortaron a medida y la instalaron en 2 horas. El color quedó exactamente igual al original.',
    author: 'Luis F.',
    role: 'Particular, Málaga Capital',
    rating: 5,
    icon: 'ri-home-smile-line',
  },
];

export default function Testimonios() {
  const { ref, isVisible } = useScrollReveal();
  const [visibleCount, setVisibleCount] = useState(3);
  const displayed = testimonios.slice(0, visibleCount);
  const hasMore = visibleCount < testimonios.length;

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts[0][0] + (parts[1] ? parts[1][0] : '');
  };

  const getColor = (index: number) => {
    const colors = [
      'bg-primary-100 text-primary-700',
      'bg-accent-100 text-accent-700',
      'bg-secondary-100 text-secondary-700',
      'bg-primary-100 text-primary-700',
      'bg-accent-100 text-accent-700',
      'bg-secondary-100 text-secondary-700',
    ];
    return colors[index % colors.length];
  };

  return (
    <section ref={ref} className={`w-full bg-background-50 py-16 md:py-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className="max-w-[1280px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-flex px-3 py-1.5 rounded-full bg-accent-100/80 text-accent-800 text-xs font-semibold font-label tracking-wide mb-4">
            Testimonios reales
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight">
            Lo que dicen nuestros clientes
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto mt-4 leading-relaxed">
            Más de 500 clientes satisfechos en Málaga y alrededores. Estas son algunas de sus opiniones.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {displayed.map((t, i) => (
            <div
              key={t.author}
              className="flex flex-col gap-5 p-6 md:p-7 rounded-2xl bg-white border border-background-200/60 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
            >
              {/* Stars + Icon */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: t.rating }).map((_, si) => (
                    <i key={si} className="ri-star-fill text-accent-400 text-sm"></i>
                  ))}
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getColor(i)}`}>
                  <i className={`${t.icon} text-lg`}></i>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="text-base text-foreground-700 leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-3 pt-3 border-t border-background-100">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getColor(i)}`}>
                  {getInitials(t.author)}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-heading font-bold text-foreground-900">
                    {t.author}
                  </span>
                  <span className="text-xs text-foreground-500">
                    {t.role}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More / CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-10 md:pt-12">
          {hasMore && (
            <button
              onClick={() => setVisibleCount((c) => c + 3)}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-background-100 border border-background-200 text-foreground-700 text-sm font-bold rounded-full hover:bg-background-200 hover:text-primary-500 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-arrow-down-line text-lg"></i>
              <span>Ver más opiniones</span>
            </button>
          )}
          <a
            href="https://wa.me/34627213347?text=Hola,%20me%20gustaría%20solicitar%20un%20presupuesto%20para%20mi%20toldo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-primary-500 text-white text-sm font-bold rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-lg shadow-primary-500/20 hover:shadow-primary-500/30 hover:scale-105"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            <span>Tú también, cuéntanos tu proyecto</span>
          </a>
        </div>
      </div>
    </section>
  );
}